import React, { useState } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  MY_ORDERS_FULL_QUERY, BUYER_SELLER_LEDGER_QUERY,
  RATE_ORDER_MUTATION, REORDER_MUTATION,
} from '../graphql';
import { toCSV, downloadCSV, groupByPeriod } from '../utils/csv';
import { personaLabel } from '../config/personas';

const money = (amt, cur) => `${(amt / 100).toFixed(2)} ${cur || ''}`.trim();
const RATED = ['delivered', 'completed'];

function Stars({ value, onRate }) {
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <span key={n} onClick={onRate ? () => onRate(n) : undefined}
          style={{ cursor: onRate ? 'pointer' : 'default', color: n <= (value || 0) ? '#f59e0b' : 'var(--border)', fontSize: 18 }}>★</span>
      ))}
    </span>
  );
}

export default function MyOrders({ onAuthRequired }) {
  const { currentUser } = useAuth();
  const { data, loading, refetch } = useQuery(MY_ORDERS_FULL_QUERY, { skip: !currentUser, fetchPolicy: 'cache-and-network' });
  const [openSeller, setOpenSeller] = useState(null);
  const [ratingDraft, setRatingDraft] = useState({}); // orderId -> { rating, feedback }
  const [loadLedger, ledgerState] = useLazyQuery(BUYER_SELLER_LEDGER_QUERY, { fetchPolicy: 'network-only' });
  const [rateOrder] = useMutation(RATE_ORDER_MUTATION, { onCompleted: () => refetch() });
  const [reorder, { loading: reordering }] = useMutation(REORDER_MUTATION, { onCompleted: () => refetch() });

  if (!currentUser) return (
    <div className="page"><div className="container" style={{ paddingTop: 100, textAlign: 'center' }}>
      <p style={{ marginBottom: 16 }}>Please sign in to view your orders.</p>
      <button className="btn btn-primary" onClick={() => onAuthRequired?.('login')}>Sign in</button>
    </div></div>
  );

  const acct = data?.myBuyerAccount;
  const sellers = data?.buyerSellers || [];
  const orders = data?.myOrders || [];

  const toggleSeller = (sellerId) => {
    if (openSeller === sellerId) { setOpenSeller(null); return; }
    setOpenSeller(sellerId);
    loadLedger({ variables: { sellerId } });
  };

  const submitRating = async (orderId) => {
    const d = ratingDraft[orderId];
    if (!d?.rating) return;
    try { await rateOrder({ variables: { orderId, rating: d.rating, feedback: d.feedback || '' } }); }
    catch (e) { alert(e.message); }
  };

  const exportOrders = (period) => {
    const rows = orders.map(o => ({
      date: new Date(o.createdAt).toISOString().slice(0, 10),
      product: o.product?.name || o.productName || '',
      seller: o.sellerName || '',
      qty: o.quantity, coins: o.totalCoins, amount: (o.totalAmount / 100).toFixed(2),
      currency: o.currency, method: o.paymentMethod, status: o.status,
      rating: o.rating || '', feedback: o.feedback || '',
    }));
    if (period === 'orders') {
      downloadCSV(`my-orders-${Date.now()}`, toCSV(rows, [
        { key: 'date', label: 'Date' }, { key: 'product', label: 'Product' }, { key: 'seller', label: 'Seller' },
        { key: 'qty', label: 'Qty' }, { key: 'coins', label: 'Coins' }, { key: 'amount', label: 'Amount' },
        { key: 'currency', label: 'Currency' }, { key: 'method', label: 'Method' }, { key: 'status', label: 'Status' },
        { key: 'rating', label: 'Rating' }, { key: 'feedback', label: 'Feedback' },
      ]));
    } else {
      const buckets = groupByPeriod(orders, period);
      downloadCSV(`my-orders-${period}-${Date.now()}`, toCSV(buckets, [
        { key: 'period', label: period === 'month' ? 'Month' : period === 'week' ? 'Week' : 'Day' },
        { key: 'orders', label: 'Orders' }, { key: 'totalCoins', label: 'Total Coins' },
        { key: 'totalAmount', label: 'Total Amount', format: v => (v / 100).toFixed(2) },
      ]));
    }
  };

  const stat = (label, value, icon, color) => (
    <div className="card" style={{ textAlign: 'center', padding: '16px 12px' }}>
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div className="page">
      <div className="container" style={{ padding: '32px 16px', maxWidth: 860 }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 4 }}>🧾 My Orders & Account</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>One account across every seller — track spend, reorder, and rate.</p>
        </div>

        {/* Account summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 20 }}>
          {stat('Total Orders', acct?.orderCount ?? 0, '📦', 'var(--primary)')}
          {stat('Sellers', acct?.sellerCount ?? 0, '🏪', 'var(--warning)')}
          {stat('Total Spent', money(acct?.totalAmount ?? 0, currentUser.currency), '💷', 'var(--success)')}
        </div>

        {/* CSV export */}
        <div className="card" style={{ marginBottom: 20, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: 'var(--font-sm)' }}>⬇ Excel/CSV record:</span>
          <button className="btn btn-ghost btn-sm" onClick={() => exportOrders('orders')}>All orders</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportOrders('day')}>Day-wise</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportOrders('week')}>Week-wise</button>
          <button className="btn btn-ghost btn-sm" onClick={() => exportOrders('month')}>Month-wise</button>
        </div>

        {/* Sellers with drill-down */}
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 style={{ fontWeight: 800, fontSize: 'var(--font-lg)', marginBottom: 12 }}>🏪 Sellers you've bought from</h2>
          {sellers.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>No purchases yet.</p>
          ) : sellers.map(s => (
            <div key={s.sellerId} style={{ borderBottom: '1px solid var(--border)', padding: '10px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => toggleSeller(s.sellerId)}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: s.avatarColor, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, flexShrink: 0 }}>{s.name[0]}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{s.name} {s.storePersona ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {personaLabel(s.storePersona)}</span> : null}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {s.orderCount} orders · {money(s.totalAmount, currentUser.currency)}
                    {s.sellerAvgRating != null && <> · seller ⭐ {s.sellerAvgRating}</>}
                    {s.myAvgRating != null && <> · <span style={{ color: '#f59e0b' }}>my ⭐ {s.myAvgRating}</span></>}
                  </div>
                </div>
                <span style={{ color: 'var(--text-muted)' }}>{openSeller === s.sellerId ? '▲' : '▼'}</span>
              </div>
              {openSeller === s.sellerId && (
                <div style={{ paddingLeft: 44, marginTop: 8 }}>
                  {ledgerState.loading ? <div className="spinner" /> : (ledgerState.data?.buyerSellerLedger || []).map(o => (
                    <div key={o.id} style={{ fontSize: 12, color: 'var(--text-muted)', padding: '3px 0' }}>
                      {new Date(o.createdAt).toLocaleDateString()} · {o.product?.name || o.productName} · {o.totalCoins} coins · <span style={{ textTransform: 'capitalize' }}>{o.status}</span>
                      {o.rating ? <span style={{ color: '#f59e0b' }}> · ★{o.rating}</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Orders list */}
        <div className="card">
          <h2 style={{ fontWeight: 800, fontSize: 'var(--font-lg)', marginBottom: 12 }}>📦 Order history</h2>
          {loading && orders.length === 0 ? (
            <div className="loading-center"><div className="spinner" /></div>
          ) : orders.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>No orders yet.</p>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {orders.map(o => {
                const canRate = RATED.includes(o.status);
                const draft = ratingDraft[o.id] || { rating: o.rating || 0, feedback: o.feedback || '' };
                return (
                  <div key={o.id} style={{ padding: '12px 14px', background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      {o.product?.imageUrl
                        ? <img src={o.product.imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
                        : <span style={{ fontSize: 22 }}>{o.product?.imageEmoji || '📦'}</span>}
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ fontWeight: 700 }}>{o.product?.name || o.productName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {o.sellerName ? `${o.sellerName} · ` : ''}{new Date(o.createdAt).toLocaleDateString()} · qty {o.quantity} · {o.totalCoins} coins
                        </div>
                      </div>
                      <span className="badge" style={{ textTransform: 'capitalize', background: o.status === 'cancelled' ? 'rgba(239,68,68,0.15)' : 'var(--primary-light)', color: o.status === 'cancelled' ? 'var(--danger)' : 'var(--primary)' }}>{o.status}</span>
                      <button className="btn btn-ghost btn-sm" disabled={reordering} onClick={() => reorder({ variables: { orderId: o.id } }).catch(e => alert(e.message))}>🔁 Reorder</button>
                    </div>
                    {/* Rating row */}
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border)' }}>
                      {o.rating ? (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Your rating: <Stars value={o.rating} /> {o.feedback && <em>— "{o.feedback}"</em>}</div>
                      ) : canRate ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Stars value={draft.rating} onRate={n => setRatingDraft(d => ({ ...d, [o.id]: { ...draft, rating: n } }))} />
                          <input className="input" style={{ flex: 1, minWidth: 140, padding: '6px 10px' }} placeholder="One line of feedback (optional)"
                            value={draft.feedback} onChange={e => setRatingDraft(d => ({ ...d, [o.id]: { ...draft, feedback: e.target.value } }))} />
                          <button className="btn btn-primary btn-sm" disabled={!draft.rating} onClick={() => submitRating(o.id)}>Rate</button>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>You can rate this once it's delivered.</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
